import { useMemo } from "react"
import type {
  ConditionGroup,
  IntakeFormGroupKey,
  IntakeFormLayoutKey,
  IntakeFormSummary,
  NextPageParams,
} from "@/schema"
import { useDataTableStore } from "@/stores/data-table-store"
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

import {
  fetchGroupedIntakeForms,
  fetchIntakeForm,
  fetchIntakeFormAggregatedResponses,
  fetchIntakeFormInvitation,
  fetchIntakeFormInvitationDraft,
  fetchIntakeFormInvitationForm,
  fetchIntakeFormInvitations,
  fetchIntakeFormPreview,
  fetchIntakeForms,
  fetchIntakeFormSubmission,
  fetchIntakeFormSubmissions,
  fetchIntakeFormTemplatePreview,
  fetchIntakeFormTemplates,
  fetchSharedIntakeForm,
} from "./api"

const firstPage = { page: 1, per_page: 25 } satisfies NextPageParams
const SENDING_FAST_POLL_WINDOW_MS = 30_000
const SENDING_POLL_WINDOW_MS = 120_000
const DELIVERY_RETRY_POLL_WINDOW_MS = 30 * 60_000

export function useFetchIntakeFormTemplates() {
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["intake_form_templates"],
    queryFn: async () => fetchIntakeFormTemplates(await getContext()),
  })
}

export function useFetchIntakeFormTemplatePreview(templateKey: string | null, layoutKey: IntakeFormLayoutKey | null) {
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["intake_form_template_preview", templateKey, layoutKey],
    queryFn: async () =>
      fetchIntakeFormTemplatePreview(await getContext(), templateKey as string, layoutKey as IntakeFormLayoutKey),
    enabled: Boolean(templateKey && layoutKey),
    staleTime: 60_000,
  })
}

export function useInfiniteIntakeForms(enabled = true) {
  const getContext = useApiContext()
  const bookId = useBookId()
  return useInfiniteQuery({
    queryKey: ["intake_forms", "infinite", bookId],
    queryFn: async ({ pageParam }) => fetchIntakeForms(await getContext(), pageParam),
    initialPageParam: firstPage,
    getNextPageParam: (lastPage) => lastPage.pagination?.next_page_params ?? undefined,
    enabled,
    select: (data) => ({
      intake_forms: data.pages.flatMap((page) => page.intake_forms),
      pagination: data.pages.at(-1)?.pagination,
    }),
  })
}

export function useInfiniteGroupedIntakeForms(enabled = true) {
  const getContext = useApiContext()
  const bookId = useBookId()
  const { completedConditions, searchTerm, viewSort } = useDataTableStore()
  const conditionGroup = useMemo<ConditionGroup>(
    () => ({ combinator: "and", conditions: completedConditions }),
    [completedConditions]
  )

  return useInfiniteQuery({
    queryKey: ["intake_forms", "grouped", bookId, searchTerm, conditionGroup, viewSort],
    queryFn: async ({ pageParam }) =>
      fetchGroupedIntakeForms(await getContext(), conditionGroup, searchTerm, pageParam, viewSort),
    placeholderData: keepPreviousData,
    initialPageParam: firstPage,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params ?? undefined,
    enabled,
    select: (data) => {
      const mergedGroups = new Map<IntakeFormGroupKey, { intake_forms: IntakeFormSummary[]; total_count: number }>()

      data.pages.forEach((page) => {
        Object.entries(page.groups).forEach(([groupKey, group]) => {
          const key = groupKey as IntakeFormGroupKey
          const mergedGroup = mergedGroups.get(key) ?? { intake_forms: [], total_count: group.total_count }
          mergedGroup.intake_forms.push(...group.intake_forms)
          mergedGroups.set(key, mergedGroup)
        })
      })

      return Array.from(mergedGroups.entries()).flatMap(([groupKey, group]) =>
        group.intake_forms.map((intakeForm) => ({
          ...intakeForm,
          groupKey,
          groupKeyTotalCount: group.total_count,
        }))
      )
    },
  })
}

export function useFetchIntakeFormsForFilters(enabled = true, bookIdOverride?: string) {
  const routeBookId = useBookId()
  const bookId = bookIdOverride ?? routeBookId
  const getContext = useApiContext(bookId)

  return useQuery({
    queryKey: ["intake_forms", "filter_options", bookId],
    queryFn: async () => {
      const context = await getContext()
      const intakeForms: IntakeFormSummary[] = []
      let page: NextPageParams | undefined = { page: 1, per_page: 100 }

      while (page) {
        const response = await fetchIntakeForms(context, page)
        intakeForms.push(...response.intake_forms)
        page = response.pagination?.next_page_params ?? undefined
      }

      return intakeForms
    },
    enabled: enabled && Boolean(bookId),
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  })
}

export function useFetchIntakeForm(id: string | null) {
  const getContext = useApiContext()
  const bookId = useBookId()
  return useQuery({
    queryKey: ["intake_form", bookId, id],
    queryFn: async () => fetchIntakeForm(await getContext(), id as string),
    enabled: Boolean(id),
    refetchInterval: (query) => {
      const form = query.state.data
      const generatingSchema =
        form?.status === "drafting_schema" && form.layout_key === null && Object.keys(form.schema).length === 0
      const designingSimple = form?.status === "drafting_schema" && form.layout_key === "simple"
      return (generatingSchema || designingSimple) && !form?.generation_error ? 2_000 : false
    },
  })
}

export function useFetchSharedIntakeForm(guid: string | null) {
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["shared_intake_form", guid],
    queryFn: async () => fetchSharedIntakeForm(await getContext(), guid as string),
    enabled: Boolean(guid),
  })
}

export function useFetchIntakeFormPreview(
  id: string | null,
  householdId?: string | null,
  published?: boolean,
  schedulingEventTypeId?: string | null
) {
  const getContext = useApiContext()
  const bookId = useBookId()
  return useQuery({
    queryKey: ["intake_form_preview", bookId, id, householdId, published, schedulingEventTypeId],
    queryFn: async () =>
      fetchIntakeFormPreview(await getContext(), id as string, householdId, published, schedulingEventTypeId),
    enabled: Boolean(id),
    placeholderData: keepPreviousData,
  })
}

export function useInfiniteIntakeFormInvitations(enabled = true) {
  const getContext = useApiContext()
  const bookId = useBookId()
  const { completedConditions, viewSort } = useDataTableStore()
  const conditionGroup = useMemo<ConditionGroup>(
    () => ({ combinator: "and", conditions: completedConditions }),
    [completedConditions]
  )

  return useInfiniteQuery({
    queryKey: ["intake_form_invitations", "infinite", bookId, conditionGroup, viewSort],
    queryFn: async ({ pageParam }) =>
      fetchIntakeFormInvitations(await getContext(), conditionGroup, pageParam, viewSort),
    initialPageParam: firstPage,
    getNextPageParam: (lastPage) => lastPage.pagination?.next_page_params ?? undefined,
    enabled,
    select: (data) => ({
      intake_form_invitations: data.pages.flatMap((page) => page.intake_form_invitations),
      pagination: data.pages.at(-1)?.pagination,
    }),
  })
}

export function useInfiniteIntakeFormInvitationsForForm(
  intakeFormId: string | null,
  activity: "all" | "opened" = "all"
) {
  const getContext = useApiContext()
  const bookId = useBookId()

  return useInfiniteQuery({
    queryKey: ["intake_form_invitations", "infinite", bookId, { intakeFormId, activity }],
    queryFn: async ({ pageParam }) =>
      fetchIntakeFormInvitations(await getContext(), undefined, pageParam, undefined, intakeFormId as string, activity),
    initialPageParam: firstPage,
    getNextPageParam: (lastPage) => lastPage.pagination?.next_page_params ?? undefined,
    enabled: Boolean(intakeFormId),
    select: (data) => ({
      intake_form_invitations: data.pages.flatMap((page) => page.intake_form_invitations),
      pagination: data.pages.at(-1)?.pagination,
    }),
  })
}

export function useInfiniteIntakeFormInvitationsForHousehold(enabled: boolean, householdId: string) {
  const getContext = useApiContext()

  return useInfiniteQuery({
    queryKey: ["intake_form_invitations", "infinite", { householdId, withoutSubmission: true }],
    queryFn: async ({ pageParam }) =>
      fetchIntakeFormInvitations(
        await getContext(),
        undefined,
        pageParam,
        undefined,
        undefined,
        "all",
        householdId,
        true
      ),
    initialPageParam: firstPage,
    getNextPageParam: (lastPage) => lastPage.pagination?.next_page_params ?? undefined,
    enabled,
    select: (data) => ({
      intake_form_invitations: data.pages.flatMap((page) => page.intake_form_invitations),
      pagination: data.pages.at(-1)?.pagination,
    }),
  })
}

export function useFetchIntakeFormInvitation(id: string | null) {
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["intake_form_invitation", id],
    queryFn: async () => fetchIntakeFormInvitation(await getContext(), id as string),
    enabled: Boolean(id),
    // Poll briefly after creation while the initial invitation email is delivered in the background.
    refetchInterval: (query) => {
      const invitation = query.state.data
      if (!invitation || invitation.state !== "open") return false

      const invitationAge = Date.now() - new Date(invitation.created_at).getTime()
      const hasPendingDelivery = ["pending", "scheduled"].includes(invitation.delivery_status)
      const hasRetryableFailure = invitation.delivery_status === "failed"
      if (hasPendingDelivery && invitationAge < SENDING_FAST_POLL_WINDOW_MS) return 1_000
      if (hasPendingDelivery && invitationAge < SENDING_POLL_WINDOW_MS) return 5_000
      if (hasRetryableFailure && invitationAge < DELIVERY_RETRY_POLL_WINDOW_MS) return 15_000
      return false
    },
  })
}

export function useFetchIntakeFormInvitationDraft(id: string | null) {
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["intake_form_invitation_draft", id],
    queryFn: async () => fetchIntakeFormInvitationDraft(await getContext(), id as string),
    enabled: Boolean(id),
  })
}

export function useFetchIntakeFormInvitationForm(id: string | null) {
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["intake_form_invitation_form", id],
    queryFn: async () => fetchIntakeFormInvitationForm(await getContext(), id as string),
    enabled: Boolean(id),
  })
}

export function useInfiniteIntakeFormSubmissions(enabled = true) {
  const getContext = useApiContext()
  const bookId = useBookId()
  const { completedConditions, viewSort } = useDataTableStore()
  const conditionGroup = useMemo<ConditionGroup>(
    () => ({ combinator: "and", conditions: completedConditions }),
    [completedConditions]
  )

  return useInfiniteQuery({
    queryKey: ["intake_form_submissions", "infinite", bookId, conditionGroup, viewSort],
    queryFn: async ({ pageParam }) =>
      fetchIntakeFormSubmissions(await getContext(), pageParam, undefined, "all", undefined, conditionGroup, viewSort),
    initialPageParam: firstPage,
    getNextPageParam: (lastPage) => lastPage.pagination?.next_page_params ?? undefined,
    enabled,
    select: (data) => ({
      intake_form_submissions: data.pages.flatMap((page) => page.intake_form_submissions),
      pagination: data.pages.at(-1)?.pagination,
    }),
  })
}

export function useInfiniteIntakeFormSubmissionsForForm(
  intakeFormId: string | null,
  activity: "all" | "needs_review" = "all"
) {
  const getContext = useApiContext()
  const bookId = useBookId()
  return useInfiniteQuery({
    queryKey: ["intake_form_submissions", "infinite", bookId, { intakeFormId, activity }],
    queryFn: async ({ pageParam }) =>
      fetchIntakeFormSubmissions(await getContext(), pageParam, intakeFormId as string, activity),
    initialPageParam: firstPage,
    getNextPageParam: (lastPage) => lastPage.pagination?.next_page_params ?? undefined,
    enabled: Boolean(intakeFormId),
    select: (data) => ({
      intake_form_submissions: data.pages.flatMap((page) => page.intake_form_submissions),
      pagination: data.pages.at(-1)?.pagination,
    }),
  })
}

export function useFetchIntakeFormAggregatedResponses(intakeFormId: string | null) {
  const getContext = useApiContext()
  const bookId = useBookId()
  return useQuery({
    queryKey: ["intake_form_aggregated_responses", bookId, intakeFormId],
    queryFn: async () => fetchIntakeFormAggregatedResponses(await getContext(), intakeFormId as string),
    enabled: Boolean(intakeFormId),
  })
}

export function useInfiniteIntakeFormSubmissionsForHousehold(enabled: boolean, householdId: string) {
  const getContext = useApiContext()
  return useInfiniteQuery({
    queryKey: ["intake_form_submissions", "infinite", { householdId }],
    queryFn: async ({ pageParam }) =>
      fetchIntakeFormSubmissions(await getContext(), pageParam, undefined, "all", householdId),
    initialPageParam: firstPage,
    getNextPageParam: (lastPage) => lastPage.pagination?.next_page_params ?? undefined,
    enabled,
    select: (data) => ({
      intake_form_submissions: data.pages.flatMap((page) => page.intake_form_submissions),
      pagination: data.pages.at(-1)?.pagination,
    }),
  })
}

export function useFetchUnreadIntakeFormSubmissions(enabled: boolean) {
  const getContext = useApiContext()
  const bookId = useBookId()
  return useQuery({
    queryKey: ["intake_form_submissions", "unread_badge", bookId],
    queryFn: async () => fetchIntakeFormSubmissions(await getContext(), { page: 1, per_page: 5 }, undefined, "unread"),
    enabled,
    refetchInterval: 60_000,
  })
}

export function useFetchIntakeFormSubmission(
  id: string | null,
  targetId?: string | null,
  additionalMappings: Record<string, string> = {},
  personMappings: Record<string, string> = {}
) {
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["intake_form_submission", id, targetId ?? null, additionalMappings, personMappings],
    queryFn: async () =>
      fetchIntakeFormSubmission(await getContext(), id as string, targetId, additionalMappings, personMappings),
    enabled: Boolean(id),
    // Documents are copied to the linked storage folder after the submission is applied.
    refetchInterval: (query) => {
      const submission = query.state.data
      if (!submission?.applied) return false
      if (submission.planning_deliverable?.status === "building") return 2_000

      return submission.documents.some((document) => ["pending", "uploading"].includes(document.drive_status))
        ? 3_000
        : false
    },
  })
}
